import { getActiveExperimentsHeaderTab } from '@/reducers/ui/projectsUiReducer';
import { useExperimentsGroups } from '@experiment-management-shared';
import useGroupAggregations from '@experiment-management-shared/api/useGroupAggregations';
import {
  DashboardView,
  DashboardViewUpdatePayload
} from '@experiment-management-shared/types';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useDashboardApiContext } from '@projects/components/EMDashboardPage/useDashboardAPIContext';

type UseEMDashboardGroupsOpts = {
  dashboardView: DashboardView | null;
  refetchInterval: number | false;
  isArchiveSection: boolean;
  updateDashboardView: (data: DashboardViewUpdatePayload) => void;
};
const useEMDashboardGroups = ({
  dashboardView,
  refetchInterval,
  isArchiveSection,
  updateDashboardView
}: UseEMDashboardGroupsOpts) => {
  const { extraColumns, isLoadingExtraColumns } = useDashboardApiContext();

  const activeExperimentsHeaderTab = useSelector(getActiveExperimentsHeaderTab);

  const isAllExperimentsTab = activeExperimentsHeaderTab === 0;

  const groups = useMemo(() => {
    const grouping = dashboardView?.table.columnGrouping || [];

    return isAllExperimentsTab ? grouping : [];
  }, [dashboardView?.table.columnGrouping, isAllExperimentsTab]);

  const applyChangeExpandedGroups = useCallback(
    (newExpandedGroups: string[]) => {
      updateDashboardView({
        table: { expandedGroups: newExpandedGroups, pageNumber: 0 }
      });
    },
    [updateDashboardView]
  );

  const {
    isFetchingExperimentGroups,
    totalGroups,
    experimentsGroupsMap
  } = useExperimentsGroups({
    expandedGroups: dashboardView?.table.expandedGroups || [],
    columnGrouping: groups,
    isArchive: isArchiveSection,
    view: dashboardView,
    onExpand: applyChangeExpandedGroups,
    queryConfig: {
      refetchInterval,
      refetchOnMount: true
    },
    columns: extraColumns,
    isLoadingColumns: isLoadingExtraColumns
  });

  const { data: groupAggregations } = useGroupAggregations(
    {
      groups,
      view: dashboardView,
      columns: extraColumns,
      isLoadingColumns: isLoadingExtraColumns
    },
    { refetchInterval, refetchOnMount: true }
  );

  const groupsQuery = groups.length ? experimentsGroupsMap.path : undefined;

  return {
    groupsQuery,
    groups,
    groupAggregations,
    experimentsGroupsMap,
    isFetchingExperimentGroups,
    totalGroups
  };
};

export default useEMDashboardGroups;
